import { useLocalStorage } from '@mantine/hooks'
import { cloneDeep, get } from 'lodash-es'
import { SetStateAction, useCallback, useMemo } from 'react'

export interface UserPreference {
	isActionTextBlurTransitionEffectEnabled: boolean
	theme: 'DARK' | 'LIGHT' | 'AUTO'
	isPostDetailSideSheetEnabled: false
}
const INTIAL_VALUES: UserPreference = {
	isActionTextBlurTransitionEffectEnabled: false,
	theme: 'AUTO',
	isPostDetailSideSheetEnabled: false,
}
export default function useUserPreference<Key extends keyof UserPreference>(key: Key) {
	const [preferences, setPreferences, removePreferences] = useLocalStorage<UserPreference>({
		key: 'JK_USER_PERFS',
		defaultValue: INTIAL_VALUES,
		sync: false,
		getInitialValueInEffect: false,
	})

	const value = useMemo(() => get(preferences, key), [preferences, key])
	const setValue = useCallback(
		(nextUserPrefValue: SetStateAction<UserPreference[Key]>) => {
			setPreferences((prev = INTIAL_VALUES) => {
				const next = cloneDeep(prev)

				if (typeof nextUserPrefValue === 'function') {
					nextUserPrefValue = nextUserPrefValue(next[key])
				}

				next[key] = nextUserPrefValue
				return next
			})
		},
		[key, setPreferences]
	)

	const removeValue = useCallback(() => {
		setPreferences((prev = INTIAL_VALUES) => {
			const next = cloneDeep(prev)
			delete next[key]
			return next
		})
	}, [key, setPreferences])

	return [value, setValue, removeValue] as const
}
